// src/components/teacher/TeacherReports.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import { ReportIcon, StudentsIcon, CalendarIcon, BookIcon } from "../shared/icons/Icons";
import { USE_DUMMY_DATA, DUMMY_STUDENTS, DUMMY_TIMETABLE } from "./dummyData";

const REPORT_CARDS = [
  {
    key: "overview",
    title: "Report Overview",
    description: "Students, classes, timetable load, and quick totals.",
    color: "#1d4ed8",
    bgColor: "rgba(29, 78, 216, 0.1)",
    icon: ReportIcon,
  },
  {
    key: "students",
    title: "Student Analysis",
    description: "Active and withdrawn students by your assigned classes.",
    color: "#16a34a",
    bgColor: "rgba(22, 163, 74, 0.1)",
    icon: StudentsIcon,
  },
  {
    key: "timetable",
    title: "Timetable Analysis",
    description: "Scheduled periods by day and course.",
    color: "#7c3aed",
    bgColor: "rgba(124, 58, 237, 0.1)",
    icon: CalendarIcon,
  },
  {
    key: "courses",
    title: "Course Load",
    description: "Course-wise teaching periods from the live timetable.",
    color: "#d97706",
    bgColor: "rgba(217, 119, 6, 0.1)",
    icon: BookIcon,
  },
];

const STATUS_COLORS = ["#16a34a", "#dc2626", "#64748b"];
const BAR_COLORS = ["#1d4ed8", "#16a34a", "#7c3aed", "#d97706", "#0891b2"];
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function compactClassName(student) {
  return [student.class_name, student.class_section].filter(Boolean).join(" ") || "Unassigned";
}

function TeacherReports() {
  const [students, setStudents] = useState(USE_DUMMY_DATA ? DUMMY_STUDENTS : []);
  const [timetable, setTimetable] = useState(USE_DUMMY_DATA ? DUMMY_TIMETABLE : []);
  const [loading, setLoading] = useState(!USE_DUMMY_DATA);
  const [activeReport, setActiveReport] = useState("overview");

  useEffect(() => {
    if (USE_DUMMY_DATA) {
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        const headers = {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        };

        const [studentsResponse, timetableResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/teachers/me/students`, { headers }),
          fetch(`${API_BASE_URL}/api/teachers/me/timetable`, { headers }),
        ]);

        if (!studentsResponse.ok || !timetableResponse.ok) {
          throw new Error("Failed to fetch teacher report data");
        }

        const studentsData = await studentsResponse.json();
        const timetableData = await timetableResponse.json();
        setStudents(Array.isArray(studentsData) ? studentsData : []);
        setTimetable(Array.isArray(timetableData) ? timetableData : []);
      } catch (err) {
        console.error("Failed to fetch reports:", err);
        setStudents([]);
        setTimetable([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const metrics = useMemo(() => {
    const active = students.filter((student) => student.student_status === "Active").length;
    const withdrawn = students.filter((student) => student.student_status === "Withdrawn").length;
    const classCount = new Set(students.map(compactClassName)).size;
    const courseCount = new Set(
      timetable
        .map((entry) => entry.course_name || entry.course_id)
        .filter(Boolean),
    ).size;

    return {
      students: students.length,
      active,
      withdrawn,
      classes: classCount,
      courses: courseCount,
      slots: timetable.length,
    };
  }, [students, timetable]);

  const statusData = useMemo(() => {
    const active = students.filter((student) => student.student_status === "Active").length;
    const withdrawn = students.filter((student) => student.student_status === "Withdrawn").length;
    const other = Math.max(students.length - active - withdrawn, 0);
    return [
      { name: "Active", value: active },
      { name: "Withdrawn", value: withdrawn },
      { name: "Other", value: other },
    ].filter((item) => item.value > 0);
  }, [students]);

  const classData = useMemo(() => {
    const counts = new Map();
    students.forEach((student) => {
      const key = compactClassName(student);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([name, value]) => ({ name, students: value }))
      .sort((a, b) => b.students - a.students);
  }, [students]);

  const dayData = useMemo(() => {
    return DAY_ORDER.map((day) => ({
      day: day.slice(0, 3),
      periods: timetable.filter((entry) => entry.day_of_week === day).length,
    })).filter((item) => item.periods > 0 || timetable.length === 0);
  }, [timetable]);

  const courseData = useMemo(() => {
    const counts = new Map();
    timetable.forEach((entry) => {
      const key = entry.course_name || `Course ${entry.course_id || ""}`.trim();
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([name, value]) => ({ name, periods: value }))
      .sort((a, b) => b.periods - a.periods);
  }, [timetable]);

  const activeCard = REPORT_CARDS.find((card) => card.key === activeReport) || REPORT_CARDS[0];

  if (loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading reports...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="tp-page-header">
        <div>
        </div>
      </div>

      <div className="tp-report-grid">
        {REPORT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.key}
              type="button"
              className={`tp-card tp-card--clickable tp-report-card ${
                activeReport === card.key ? "is-active-report" : ""
              }`}
              onClick={() => setActiveReport(card.key)}
            >
              <div
                className="tp-report-icon"
                style={{ background: card.bgColor, color: card.color }}
              >
                <Icon />
              </div>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
              <span className="tp-btn tp-btn--ghost">View Analysis</span>
            </button>
          );
        })}
      </div>

      <div className="tp-stats-grid">
        <div className="tp-card">
          <h3 className="tp-stat-value">{metrics.students}</h3>
          <p className="tp-stat-label">Students</p>
        </div>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#16a34a" }}>{metrics.active}</h3>
          <p className="tp-stat-label">Active</p>
        </div>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#1d4ed8" }}>{metrics.classes}</h3>
          <p className="tp-stat-label">Classes</p>
        </div>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#7c3aed" }}>{metrics.slots}</h3>
          <p className="tp-stat-label">Timetable Slots</p>
        </div>
      </div>

      <div className="tp-card tp-report-detail">
        <div className="tp-page-header">
          <div>
            <h2>{activeCard.title}</h2>
            <p>{activeCard.description}</p>
          </div>
          <span className="tp-badge">{metrics.courses} courses</span>
        </div>

        {activeReport === "overview" && (
          <div className="tp-chart-grid">
            <ChartCard title="Student Status">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} label>
                      {statusData.map((entry, index) => (
                        <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart text="No student data available." />
              )}
            </ChartCard>

            <ChartCard title="Weekly Timetable Load">
              <BarResponsive data={dayData} dataKey="periods" nameKey="day" color="#7c3aed" />
            </ChartCard>
          </div>
        )}

        {activeReport === "students" && (
          <div className="tp-chart-grid">
            <ChartCard title="Students By Class">
              <BarResponsive data={classData} dataKey="students" nameKey="name" color="#1d4ed8" />
            </ChartCard>
            <ChartCard title="Student Status">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} label>
                      {statusData.map((entry, index) => (
                        <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart text="No student data available." />
              )}
            </ChartCard>
          </div>
        )}

        {activeReport === "timetable" && (
          <div className="tp-chart-grid">
            <ChartCard title="Periods By Day">
              <BarResponsive data={dayData} dataKey="periods" nameKey="day" color="#7c3aed" />
            </ChartCard>
            <ChartCard title="Periods By Course">
              <BarResponsive data={courseData} dataKey="periods" nameKey="name" color="#d97706" />
            </ChartCard>
          </div>
        )}

        {activeReport === "courses" && (
          <div className="tp-chart-grid">
            <ChartCard title="Course Load">
              <BarResponsive data={courseData} dataKey="periods" nameKey="name" color="#d97706" />
            </ChartCard>
            <ChartCard title="Class Coverage">
              <BarResponsive data={classData} dataKey="students" nameKey="name" color="#16a34a" />
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="tp-card tp-chart-card">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ text }) {
  return <div className="tp-empty tp-empty--compact">{text}</div>;
}

function BarResponsive({ data, dataKey, nameKey, color }) {
  if (!data || data.length === 0) {
    return <EmptyChart text="No data available yet." />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 12, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} interval={0} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={`${entry[nameKey]}-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default TeacherReports;
